import { useState } from 'react';

/**
 * Provides functionality for managing an adaptive menu. This includes
 * handling the open/close state of the menu and managing the element it is anchored to.
 *
 * @returns {object} An object containing:
 * - `anchorEl` (HTMLElement | null): The current anchor element for the menu, or null if no menu is open.
 * - `open` (boolean): The current open/closed state of the menu.
 * - `handleOpen` (function): A function to open the menu and set the anchor element, expects an event as an argument.
 * - `handleClose` (function): A function to close the menu and reset the anchor element.
 */

const useMAdaptativeMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  /**
   * Handles opening of the menu. Sets the event's current target as the anchor element
   * and opens the menu. If the event does not have a current target, logs an error.
   *
   * @param {Object} event - The event object from a click event. Expected to have a `currentTarget` property.
   */
  const handleOpen = (event) => {
    if (event) {
      event.stopPropagation();
    } // Prevent from firing parent onClick event
    if (!event?.currentTarget) {
      console.warn('useMAdaptativeMenu handleOpen : missing event props');
      return;
    }
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  /**
   * Handles closing of the menu. Resets the anchor element to null and closes the menu.
   */
  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
    } // Prevent from firing parent onClick event
    setAnchorEl(null);
    setOpen(false);
  };

  return { anchorEl, open, handleOpen, handleClose };
};

export default useMAdaptativeMenu;
